/**
 * The Settings gump (settings-overhaul; grew out of the issue-#184 corner dropdown):
 * the cog in the top-right toolbar opens a centred, draggable tabbed window on the
 * shared window chassis ({@link createUiWindow}, ADR-0271) with a vertical tab
 * sidebar ({@link createTabs}) and a cross-tab search field in the title bar.
 * Built as **reusable settings infrastructure** - the sound/music controls
 * (`sound.ts` / `music.ts`, ADR-0105/0146), the display + gameplay toggles
 * (`blood.ts`, `alwaysRun.ts`, the low-power checkbox) and the ignore list all plug
 * into panes this shell owns, keyed by the same container ids as before the
 * overhaul, so this module owns the shell + keybindings/ignore rows, not the
 * sections' own wiring.
 *
 * Like `sound.ts` / `chat.ts`, this module owns **only** the DOM wiring; the
 * markup and styling live in `index.html`, and it touches no Pixi and no socket, so
 * it unit-tests headlessly (jsdom). Rows are built with `createElement`/`textContent`
 * only - the same XSS-safe discipline as `chat.ts` and the wardrobe list.
 *
 * **Search** filters every tab at once: while the query is non-empty the tab
 * sidebar's selection is moot and every pane shows only its matching rows (a
 * section whose heading matches keeps all its rows). Clearing the query - or
 * clicking any tab - returns to the plain single-pane view. Dynamic rows
 * (keybindings, ignore names) re-apply the active filter on every re-render.
 *
 * Rebinding is a capture flow: "Rebind" arms a document-level capture-phase keydown
 * listener that swallows the next keypress (so the armed key never leaks to the
 * global hotkey dispatch in `main.ts`), turns it into a {@link KeyBinding}, writes it
 * through the registry ({@link saveBindings}), and re-renders. Pure modifiers are
 * skipped (wait for a real key); reserved keys (chat's Enter/Escape and the
 * movement/run keys) are refused; conflicts are surfaced inline rather than silently
 * shadowed. While a capture is armed, Escape cancels the capture (the capture-phase
 * listener stops propagation, so the window's own Escape-closes never fires).
 */
import {
  ACTIONS,
  ACTION_LABELS,
  ADMIN_ACTIONS,
  conflictingActions,
  defaultBindings,
  formatBinding,
  isModifierCode,
  isReservedCode,
  formatCode,
  saveBindings,
  type Action,
  type BindingTable,
  type KeyBinding,
} from './input/keybindings';
import type { IgnoreListHandle } from './ignoreList';
import { createUiWindow } from './ui/window';
import { createTabs } from './ui/tabs';

/** The gump's tabs, in sidebar order; each maps to a `#settings-pane-<tab>` pane. */
const SETTINGS_TABS = ['sound', 'display', 'gameplay', 'keys', 'ignore'] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];

/** Options for {@link initSettings}; `doc` is injected by tests. */
export interface SettingsOptions {
  /** The initial binding table (already loaded + merged over defaults). */
  bindings: BindingTable;
  /**
   * The per-viewer ignore list, plugged in as the "Ignore List" tab (the same
   * way the display toggles plug into this reusable shell): the pane lists each
   * ignored name with a remove control and re-renders live off the list's change
   * subscription. Optional so the keybindings-only tests can omit it.
   */
  ignoreList?: IgnoreListHandle;
  /**
   * Called with the fresh table whenever a binding changes, so `main.ts`'s live
   * dispatch table tracks a rebind/reset without a reload. Optional for tests.
   */
  onChange?: (bindings: BindingTable) => void;
  /**
   * Called whenever the gump's open-state flips, so the shared toolbar coordinator
   * (`toolbar.ts`) can enforce one-panel-at-a-time. Optional for tests.
   */
  onOpenChange?: (open: boolean) => void;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts`/tests drive the settings gump through. */
export interface SettingsHandle {
  /** The current binding table. */
  getBindings(): BindingTable;
  /** Open or close the gump programmatically. */
  setOpen(open: boolean): void;
  /** Whether the gump is currently open. */
  isOpen(): boolean;
  /**
   * Reveal (true) or hide (false) admin-only rebind rows ({@link ADMIN_ACTIONS}), ADR-0183.
   * Cosmetic: the actions still exist and the server gates their power - this just avoids
   * advertising admin features to non-admins. Off by default (pre-login / non-admin).
   */
  setAdminActionsVisible(visible: boolean): void;
}

export function initSettings(opts: SettingsOptions): SettingsHandle {
  const doc = opts.doc ?? document;
  const cog = doc.getElementById('settings-button') as HTMLButtonElement | null;
  const list = doc.getElementById('settings-keybindings');
  const notice = doc.getElementById('settings-notice');
  const search = doc.getElementById('settings-search') as HTMLInputElement | null;
  const searchEmpty = doc.getElementById('settings-search-empty');

  let bindings = opts.bindings;
  // The action currently awaiting a captured keypress, or null when idle.
  let capturing: Action | null = null;
  // Whether admin-only rebind rows are shown (ADR-0183). Default off: pre-login and for
  // a non-admin the admin-teleport row is hidden. Flipped by setAdminActionsVisible off
  // the reveal-only Welcome.isAdmin flag.
  let adminActionsVisible = false;

  const persist = (): void => {
    saveBindings(bindings);
    opts.onChange?.(bindings);
  };

  const setNotice = (text: string): void => {
    if (notice) notice.textContent = text;
  };

  // --- Tabs + search ----------------------------------------------------------------
  const panes = new Map<SettingsTab, HTMLElement | null>(
    SETTINGS_TABS.map((tab) => [tab, doc.getElementById(`settings-pane-${tab}`)]),
  );

  /**
   * Show the panes/sections/rows the current state selects: with an empty query,
   * the active tab's pane whole; with a query, every pane filtered to its matching
   * rows (a section whose heading matches keeps all its rows), plus the no-match
   * empty state. Re-run after every tab switch, keystroke, and dynamic re-render.
   */
  const applyView = (): void => {
    const query = (search?.value ?? '').trim().toLowerCase();
    const searching = query.length > 0;
    let anyMatch = false;
    for (const [tab, pane] of panes) {
      if (!pane) continue;
      let paneHasMatch = false;
      for (const section of pane.querySelectorAll<HTMLElement>('.settings-section')) {
        const headingMatches =
          (section.querySelector('h3')?.textContent ?? '').toLowerCase().includes(query);
        let sectionHasMatch = headingMatches;
        for (const row of section.querySelectorAll<HTMLElement>('.settings-row')) {
          const rowMatches =
            !searching || headingMatches || (row.textContent ?? '').toLowerCase().includes(query);
          row.hidden = !rowMatches;
          if (rowMatches) sectionHasMatch = true;
        }
        section.hidden = searching && !sectionHasMatch;
        if (sectionHasMatch) paneHasMatch = true;
      }
      pane.hidden = searching ? !paneHasMatch : tab !== tabs.value();
      if (searching && paneHasMatch) anyMatch = true;
    }
    if (searchEmpty) searchEmpty.hidden = !searching || anyMatch;
  };

  const clearSearch = (): void => {
    if (search) search.value = '';
  };

  const tabs = createTabs<SettingsTab>(
    SETTINGS_TABS.map((tab) => ({
      button: doc.getElementById(`settings-tab-${tab}`),
      value: tab,
    })),
    'sound',
    () => applyView(),
  );

  // Any click on the sidebar leaves search mode - including a click on the tab that
  // is already selected, which createTabs treats as a no-op switch. This bubbles
  // after the button's own switch handler, so the final state is always the plain
  // single-pane view of the clicked tab.
  doc.getElementById('settings-tabs')?.addEventListener('click', () => {
    if (search?.value) {
      clearSearch();
      applyView();
    }
  });

  search?.addEventListener('input', applyView);
  // The search field lives in the drag bar: keep its pointerdown from starting a
  // drag (the bar's handler preventDefaults, which would also swallow focus).
  search?.addEventListener('pointerdown', (e) => e.stopPropagation());
  // Escape in a non-empty search clears the query instead of closing the gump; a
  // second Escape (empty field) falls through to the window's Escape-closes.
  search?.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && search.value) {
      e.stopPropagation();
      clearSearch();
      applyView();
    }
  });

  // The actions that will actually be rendered as rows: for a non-admin the admin-only
  // rows (ADR-0183) are hidden, so they must also be excluded from conflict detection -
  // otherwise a visible key rebound onto the hidden admin-teleport default (bare KeyT)
  // is flagged against a row the user can neither see nor resolve.
  const visibleActions = (): Action[] =>
    ACTIONS.filter((action) => adminActionsVisible || !ADMIN_ACTIONS.has(action));

  // --- Rendering --------------------------------------------------------------------
  const render = (): void => {
    if (!list) return;
    list.textContent = '';
    const shown = visibleActions();
    const conflicts = conflictingActions(bindings, shown);
    for (const action of shown) {
      const row = doc.createElement('div');
      row.className = 'settings-row';

      const label = doc.createElement('span');
      label.className = 'settings-label';
      label.textContent = ACTION_LABELS[action];
      row.appendChild(label);

      const key = doc.createElement('span');
      key.className = 'settings-key';
      key.textContent = capturing === action ? 'Press a key…' : formatBinding(bindings[action]);
      if (conflicts.has(action)) key.classList.add('conflict');
      row.appendChild(key);

      const rebind = doc.createElement('button');
      rebind.type = 'button';
      rebind.className = 'settings-rebind';
      rebind.textContent = capturing === action ? 'Cancel' : 'Rebind';
      rebind.addEventListener('click', () => {
        if (capturing === action) cancelCapture();
        else beginCapture(action);
      });
      row.appendChild(rebind);

      const reset = doc.createElement('button');
      reset.type = 'button';
      reset.className = 'settings-reset';
      reset.textContent = 'Reset';
      reset.addEventListener('click', () => resetAction(action));
      row.appendChild(reset);

      list.appendChild(row);
    }
    // Fresh rows are unhidden; keep an active search filter applied to them.
    applyView();
  };

  // --- Capture flow -----------------------------------------------------------------
  const onCaptureKey = (e: KeyboardEvent): void => {
    if (capturing === null) return;
    // Swallow the keypress in the capture phase so the armed key never reaches the
    // global hotkey dispatch (main.ts's window keydown) or the page.
    e.preventDefault();
    e.stopPropagation();

    if (e.code === 'Escape') {
      cancelCapture();
      return;
    }
    // Wait for a non-modifier key: pressing Alt then F should bind Alt+F, not "Alt".
    if (isModifierCode(e.code)) return;
    // Pass the chord's modifiers so a Ctrl/Alt/Meta-modified press of a movement/chat
    // key (e.g. Ctrl+S) is allowed - it can never shadow bare movement - while a bare
    // (or Shift-only) press of the same key stays reserved (ADR-0144).
    if (isReservedCode(e.code, e)) {
      setNotice(`${formatCode(e.code)} is reserved - pick another key.`);
      return;
    }

    const binding: KeyBinding = { code: e.code };
    if (e.altKey) binding.alt = true;
    if (e.ctrlKey) binding.ctrl = true;
    if (e.shiftKey) binding.shift = true;
    if (e.metaKey) binding.meta = true;

    const action = capturing;
    bindings = { ...bindings, [action]: binding };
    endCapture();
    persist();
    render();
    const conflicts = conflictingActions(bindings, visibleActions());
    setNotice(
      conflicts.has(action)
        ? `Warning: ${formatBinding(binding)} is bound to more than one action.`
        : `Bound ${ACTION_LABELS[action]} to ${formatBinding(binding)}.`,
    );
  };

  const beginCapture = (action: Action): void => {
    if (capturing !== null) endCapture();
    capturing = action;
    setNotice('Press a key to bind, or Esc to cancel.');
    doc.addEventListener('keydown', onCaptureKey, true);
    render();
  };

  /** Tear down the capture listener + state without touching the notice. */
  const endCapture = (): void => {
    capturing = null;
    doc.removeEventListener('keydown', onCaptureKey, true);
  };

  const cancelCapture = (): void => {
    endCapture();
    setNotice('');
    render();
  };

  const resetAction = (action: Action): void => {
    bindings = { ...bindings, [action]: defaultBindings()[action] };
    persist();
    setNotice(`Reset ${ACTION_LABELS[action]} to ${formatBinding(bindings[action])}.`);
    render();
  };

  // --- Ignore list ------------------------------------------------------------------
  // The per-viewer ignore list pane: one row per ignored name with an × to stop
  // ignoring, re-rendered live off the list's change subscription (a right-click add
  // elsewhere, or a remove here). Built with createElement/textContent only, the same
  // XSS-safe discipline as the keybinding rows - an ignored name is shown as text.
  const ignoreList = opts.ignoreList;
  const ignoreContainer = doc.getElementById('settings-ignore');
  const renderIgnore = (): void => {
    if (!ignoreContainer || !ignoreList) return;
    ignoreContainer.textContent = '';
    const names = ignoreList.list();
    if (names.length === 0) {
      const empty = doc.createElement('p');
      empty.className = 'settings-empty';
      empty.textContent = 'No one ignored';
      ignoreContainer.appendChild(empty);
    }
    for (const name of names) {
      const row = doc.createElement('div');
      row.className = 'settings-row';

      const label = doc.createElement('span');
      label.className = 'settings-label';
      label.textContent = name;
      row.appendChild(label);

      const remove = doc.createElement('button');
      remove.type = 'button';
      remove.className = 'settings-unignore';
      remove.textContent = '×';
      remove.title = `Stop ignoring ${name}`;
      remove.setAttribute('aria-label', `Stop ignoring ${name}`);
      remove.addEventListener('click', () => ignoreList.remove(name));
      row.appendChild(remove);

      ignoreContainer.appendChild(row);
    }
    // Fresh rows are unhidden; keep an active search filter applied to them.
    applyView();
  };

  // --- The window shell -------------------------------------------------------------
  // The shared draggable-window chassis adopts the `#settings-panel` markup and owns
  // centring, title-bar drag, and the close gestures (✕ / right-click / Escape).
  // Null only for a body-less document; the handle then degrades to a no-op shell
  // (the resilience rule every window module follows).
  const win = createUiWindow({
    doc,
    id: 'settings-panel',
    title: 'Settings',
    ariaLabel: 'Settings',
    fallbackWidth: 680,
    fallbackHeight: 460,
    centerVertically: true,
    escapeCloses: true,
    clampToViewport: true,
    raiseOnOpen: true,
    onOpen: () => {
      cog?.setAttribute('aria-expanded', 'true');
      opts.onOpenChange?.(true);
    },
    onClose: () => {
      cog?.setAttribute('aria-expanded', 'false');
      cancelCapture();
      clearSearch();
      applyView();
      opts.onOpenChange?.(false);
    },
  });

  const isOpen = (): boolean => win?.isOpen() ?? false;
  const setOpen = (open: boolean): void => {
    if (!win || open === win.isOpen()) return;
    if (open) win.open();
    else win.close();
  };

  cog?.addEventListener('click', () => setOpen(!isOpen()));

  render();
  // Track live edits (added by a right-click elsewhere, removed here) with no reload.
  ignoreList?.subscribe(renderIgnore);
  renderIgnore();

  const setAdminActionsVisible = (visible: boolean): void => {
    if (visible === adminActionsVisible) return;
    adminActionsVisible = visible;
    render();
  };

  return {
    getBindings: () => bindings,
    setOpen,
    isOpen,
    setAdminActionsVisible,
  };
}
